/**
 * Competitor 360° — Phase 4 of the Great Simplification.
 *
 * One page that shows everything we know about a competitor: their
 * projects (won + lost-to-us), their product portfolio (optionally
 * mapped to our SKUs), their certifications, and a timeline of
 * observed prices.
 *
 * Drives the BD question "should we worry about them on this tender?"
 * without a dozen jumps.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Swords, ArrowLeft, Briefcase, Package, ShieldCheck, IndianRupee,
  Plus, Trash2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";


const fmtINR = (n) =>
  n == null ? "—" : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);


const STATUS_STYLE = {
  not_bid:       "bg-slate-100 text-slate-700 border-slate-200",
  bid_submitted: "bg-amber-100 text-amber-700 border-amber-200",
  lost:          "bg-rose-100 text-rose-700 border-rose-200",
  won:           "bg-emerald-100 text-emerald-700 border-emerald-200",
};


// ---------------------------------------------------------------------------
// Projects tab
// ---------------------------------------------------------------------------

function ProjectsTab({ competitorId, items, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    client_name: "", project_name: "", industry: "", sector: "", state: "",
    portal: "", value_inr: "", awarded_on: "", our_status: "not_bid", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.client_name.trim()) { toast.error("Client name required"); return; }
    setBusy(true);
    try {
      await v2Api.competitors.createProject(competitorId, {
        ...form,
        value_inr: form.value_inr === "" ? null : Number(form.value_inr),
      });
      setAdding(false);
      setForm({
        client_name: "", project_name: "", industry: "", sector: "", state: "",
        portal: "", value_inr: "", awarded_on: "", our_status: "not_bid", notes: "",
      });
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!await confirm("Delete this project entry?", { title: "Delete project entry", confirmLabel: "Delete" })) return;
    try { await v2Api.competitors.removeProject(id); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail ?? "Delete failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-500">
          Projects this competitor bid on / won — irrespective of whether we
          bid. Use ``our_status`` to mark how we did relative to them.
        </p>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add project
          </Button>
        )}
      </div>

      {adding && (
        <Card><CardContent className="py-4">
          <div className="grid grid-cols-12 gap-3 text-sm">
            <div className="col-span-6">
              <label className="text-xs text-slate-500">Client *</label>
              <Input value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                placeholder="e.g. Indian Oil Corporation" />
            </div>
            <div className="col-span-6">
              <label className="text-xs text-slate-500">Project name</label>
              <Input value={form.project_name}
                onChange={(e) => setForm({ ...form, project_name: e.target.value })} />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Industry</label>
              <Input value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })} />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Sector</label>
              <Input value={form.sector}
                onChange={(e) => setForm({ ...form, sector: e.target.value })} />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">State</label>
              <Input value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Portal</label>
              <Input value={form.portal}
                onChange={(e) => setForm({ ...form, portal: e.target.value })}
                placeholder="GeM / CPPP / direct" />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Value (₹)</label>
              <Input type="number" value={form.value_inr}
                onChange={(e) => setForm({ ...form, value_inr: e.target.value })} />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Awarded on</label>
              <Input type="date" value={form.awarded_on}
                onChange={(e) => setForm({ ...form, awarded_on: e.target.value })} />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Our status</label>
              <select value={form.our_status}
                onChange={(e) => setForm({ ...form, our_status: e.target.value })}
                className="w-full px-2 py-1.5 border rounded-md bg-white text-sm">
                <option value="not_bid">We didn't bid</option>
                <option value="bid_submitted">We bid (pending)</option>
                <option value="lost">We lost</option>
                <option value="won">We won</option>
              </select>
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Notes</label>
              <Input value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
          </div>
        </CardContent></Card>
      )}

      {items.length === 0 && !adding && (
        <p className="text-sm italic text-slate-400">No project records yet.</p>
      )}

      {items.map((p) => (
        <Card key={p.id}><CardContent className="py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-slate-900 font-medium">
                {p.client_name}
                {p.project_name ? <span className="text-slate-500"> · {p.project_name}</span> : null}
              </p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge className={`border ${STATUS_STYLE[p.our_status] || ""}`}>
                  {p.our_status.replace("_", " ")}
                </Badge>
                {p.industry && <Badge variant="outline" className="text-[10px]">{p.industry}</Badge>}
                {p.sector && <Badge variant="outline" className="text-[10px]">{p.sector}</Badge>}
                {p.state && <Badge variant="outline" className="text-[10px]">{p.state}</Badge>}
                {p.portal && <Badge variant="outline" className="text-[10px]">{p.portal}</Badge>}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {p.value_inr != null ? `₹${fmtINR(p.value_inr)}` : "value unknown"}
                {p.awarded_on ? ` · awarded ${p.awarded_on}` : ""}
                {p.notes ? ` · ${p.notes}` : ""}
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove(p.id)}>
              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
            </Button>
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Products tab
// ---------------------------------------------------------------------------

function ProductsTab({ competitorId, items, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: "", model_number: "", category: "",
    list_price_inr: "", base_price_inr: "", technical_notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Product name required"); return; }
    setBusy(true);
    try {
      await v2Api.competitors.createProduct(competitorId, {
        ...form,
        list_price_inr: form.list_price_inr === "" ? null : Number(form.list_price_inr),
        base_price_inr: form.base_price_inr === "" ? null : Number(form.base_price_inr),
      });
      setAdding(false);
      setForm({ name: "", model_number: "", category: "", list_price_inr: "", base_price_inr: "", technical_notes: "" });
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!await confirm("Delete this product?", { title: "Delete product", confirmLabel: "Delete" })) return;
    try { await v2Api.competitors.removeProduct(id); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail ?? "Delete failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-500">
          Competitor's product portfolio. Map to our SKU later for
          side-by-side BoQ comparison.
        </p>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add product
          </Button>
        )}
      </div>

      {adding && (
        <Card><CardContent className="py-4">
          <div className="grid grid-cols-12 gap-3 text-sm">
            <div className="col-span-5">
              <label className="text-xs text-slate-500">Name *</label>
              <Input value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Model #</label>
              <Input value={form.model_number}
                onChange={(e) => setForm({ ...form, model_number: e.target.value })} />
            </div>
            <div className="col-span-4">
              <label className="text-xs text-slate-500">Category</label>
              <Input value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">List price ₹</label>
              <Input type="number" value={form.list_price_inr}
                onChange={(e) => setForm({ ...form, list_price_inr: e.target.value })} />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Base price ₹</label>
              <Input type="number" value={form.base_price_inr}
                onChange={(e) => setForm({ ...form, base_price_inr: e.target.value })} />
            </div>
            <div className="col-span-6">
              <label className="text-xs text-slate-500">Technical notes</label>
              <Input value={form.technical_notes}
                onChange={(e) => setForm({ ...form, technical_notes: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
          </div>
        </CardContent></Card>
      )}

      {items.length === 0 && !adding && (
        <p className="text-sm italic text-slate-400">No products logged yet.</p>
      )}

      {items.map((p) => (
        <Card key={p.id}><CardContent className="py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-slate-900 font-medium">
                {p.name}
                {p.model_number ? <span className="text-slate-500 font-mono text-xs"> · {p.model_number}</span> : null}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {p.category || "—"}
                {p.list_price_inr != null && ` · list ₹${fmtINR(p.list_price_inr)}`}
                {p.base_price_inr != null && ` · base ₹${fmtINR(p.base_price_inr)}`}
                {p.equivalent_to_sku_id ? " · mapped to our SKU" : " · no SKU mapping"}
              </p>
              {p.technical_notes && (
                <p className="text-xs text-slate-600 mt-1">{p.technical_notes}</p>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove(p.id)}>
              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
            </Button>
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Certifications tab
// ---------------------------------------------------------------------------

const CERT_KINDS = [
  "ISO_9001", "ISO_14001", "ISO_17025", "ISO_45001",
  "BIS", "CE", "ATEX", "IECEx", "RoHS", "NABL", "REACH",
  "UL", "CSA", "OEM_AUTH", "MSME", "GeM_SELLER", "OTHER",
];

function CertificationsTab({ competitorId, items, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    kind: "ISO_9001", kind_other: "", number: "", issued_by: "",
    valid_until: "", scope_notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await v2Api.competitors.createCertification(competitorId, form);
      setAdding(false);
      setForm({ kind: "ISO_9001", kind_other: "", number: "", issued_by: "", valid_until: "", scope_notes: "" });
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!await confirm("Delete this certification?", { title: "Delete certification", confirmLabel: "Delete" })) return;
    try { await v2Api.competitors.removeCertification(id); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail ?? "Delete failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-500">
          Certifications this competitor is known to hold. Used to flip the
          eligibility question ("who CAN qualify for this tender?").
        </p>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add certification
          </Button>
        )}
      </div>

      {adding && (
        <Card><CardContent className="py-4">
          <div className="grid grid-cols-12 gap-3 text-sm">
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Kind</label>
              <select value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                className="w-full px-2 py-1.5 border rounded-md bg-white text-sm">
                {CERT_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            {form.kind === "OTHER" && (
              <div className="col-span-3">
                <label className="text-xs text-slate-500">Kind label</label>
                <Input value={form.kind_other}
                  onChange={(e) => setForm({ ...form, kind_other: e.target.value })} />
              </div>
            )}
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Number</label>
              <Input value={form.number}
                onChange={(e) => setForm({ ...form, number: e.target.value })} />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Issued by</label>
              <Input value={form.issued_by}
                onChange={(e) => setForm({ ...form, issued_by: e.target.value })} />
            </div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Valid until</label>
              <Input type="date" value={form.valid_until}
                onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
            </div>
            <div className="col-span-12">
              <label className="text-xs text-slate-500">Scope notes</label>
              <Input value={form.scope_notes}
                onChange={(e) => setForm({ ...form, scope_notes: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
          </div>
        </CardContent></Card>
      )}

      {items.length === 0 && !adding && (
        <p className="text-sm italic text-slate-400">No certifications logged yet.</p>
      )}

      {items.map((c) => (
        <Card key={c.id}><CardContent className="py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline">{c.kind === "OTHER" ? c.kind_other || "OTHER" : c.kind}</Badge>
                {c.number && <span className="font-mono text-xs text-slate-600">{c.number}</span>}
                {c.issued_by && <span className="text-xs text-slate-500">· {c.issued_by}</span>}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {c.valid_until ? `Valid until ${c.valid_until}` : "No expiry recorded"}
                {c.scope_notes ? ` · ${c.scope_notes}` : ""}
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove(c.id)}>
              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
            </Button>
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Recent prices tab (read-only — observations are logged via the SKU page)
// ---------------------------------------------------------------------------

function PricesTab({ items }) {
  if (items.length === 0) {
    return (
      <p className="text-sm italic text-slate-400">
        No price observations recorded. Log them from a SKU's Competitor
        Prices panel.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((p) => (
        <Card key={p.id}><CardContent className="py-2">
          <div className="flex items-center justify-between gap-3 text-sm">
            <div className="min-w-0">
              <p className="text-slate-800">
                {p.product_category || (p.sku_id ? `SKU ${p.sku_id.slice(0, 8)}…` : "—")}
                {" "}
                <Badge variant="outline" className="text-[10px] ml-1">
                  {p.observed_role.replace("_", " ")}
                </Badge>
              </p>
              <p className="text-xs text-slate-500">
                {p.unit_price_inr != null ? `₹${fmtINR(p.unit_price_inr)} / unit` : ""}
                {p.qty != null ? ` × ${p.qty}` : ""}
                {p.total_price_inr != null ? ` · total ₹${fmtINR(p.total_price_inr)}` : ""}
                {p.tender_portal ? ` · ${p.tender_portal}` : ""}
                {p.client_state ? ` · ${p.client_state}` : ""}
                {p.observed_on ? ` · ${p.observed_on}` : ""}
              </p>
            </div>
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function CompetitorDetailPage() {
  const { competitorId } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setDossier(await v2Api.competitors.dossier(competitorId)); }
    catch (err) { toast.error(err?.response?.data?.detail ?? "Could not load competitor"); }
    finally { setLoading(false); }
  }, [competitorId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading && !dossier) {
    return <div className="p-8 text-sm text-slate-500">Loading competitor…</div>;
  }
  if (!dossier) {
    return <div className="p-8 text-sm text-slate-500">Not found.</div>;
  }

  const c = dossier.competitor;
  const s = dossier.summary;

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <button
            onClick={() => navigate("/v2/competitors")}
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-1"
          >
            <ArrowLeft className="w-3 h-3" /> Back to competitors
          </button>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Swords className="w-5 h-5 text-rose-600" /> {c.name}
          </h1>
          <p className="text-sm text-slate-500">
            {c.sector_tags?.length > 0
              ? c.sector_tags.join(" · ")
              : "no sector tags"}
            {c.aliases?.length > 0 && ` · aka ${c.aliases.slice(0, 3).join(", ")}`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-3">
        <Card><CardContent className="py-3 text-center">
          <p className="text-2xl font-semibold tabular-nums text-slate-900">{s.projects}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Projects</p>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <p className="text-2xl font-semibold tabular-nums text-slate-900">{s.products}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Products</p>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <p className="text-2xl font-semibold tabular-nums text-slate-900">{s.certifications}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Certifications</p>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <p className="text-2xl font-semibold tabular-nums text-rose-700">{s.they_won_count}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">They beat us</p>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <p className="text-2xl font-semibold tabular-nums text-emerald-700">{s.we_won_against_them_count}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">We beat them</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="projects">
        <TabsList>
          <TabsTrigger value="projects"><Briefcase className="w-3.5 h-3.5 mr-1" /> Projects</TabsTrigger>
          <TabsTrigger value="products"><Package className="w-3.5 h-3.5 mr-1" /> Products</TabsTrigger>
          <TabsTrigger value="certifications"><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Certifications</TabsTrigger>
          <TabsTrigger value="prices"><IndianRupee className="w-3.5 h-3.5 mr-1" /> Recent prices</TabsTrigger>
        </TabsList>
        <TabsContent value="projects">
          <ProjectsTab competitorId={competitorId} items={dossier.projects} onChanged={refresh} />
        </TabsContent>
        <TabsContent value="products">
          <ProductsTab competitorId={competitorId} items={dossier.products} onChanged={refresh} />
        </TabsContent>
        <TabsContent value="certifications">
          <CertificationsTab competitorId={competitorId} items={dossier.certifications} onChanged={refresh} />
        </TabsContent>
        <TabsContent value="prices">
          <PricesTab items={dossier.recent_prices} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
